import { gql } from '@apollo/client'
import { amber, green } from '@material-ui/core/colors'
import IconButton from '@material-ui/core/IconButton'
import Snackbar, { SnackbarCloseReason } from '@material-ui/core/Snackbar'
import SnackbarContent from '@material-ui/core/SnackbarContent'
import { makeStyles, Theme } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CloseIcon from '@material-ui/icons/Close'
import ErrorIcon from '@material-ui/icons/Error'
import InfoIcon from '@material-ui/icons/Info'
import WarningIcon from '@material-ui/icons/Warning'
import clsx from 'clsx'
import React from 'react'

const useSnackbarWrapperStyles = makeStyles((theme: Theme) => ({
    snackbarContainer: {
        flexWrap: 'nowrap',
    },
    success: {
        backgroundColor: green[600],
    },
    error: {
        backgroundColor: theme.palette.error.dark,
    },
    info: {
        backgroundColor: theme.palette.primary.main,
    },
    warning: {
        backgroundColor: amber[700],
    },
    icon: {
        fontSize: 20,
    },
    iconVariant: {
        opacity: 0.9,
        marginRight: theme.spacing(1),
    },
}))

const variantIcon = {
    success: CheckCircleIcon,
    warning: WarningIcon,
    error: ErrorIcon,
    info: InfoIcon,
}

/**
 * `ShowGLobalSnackbarRequest`, `ShowGlobalSnackbarMutationProps`, and `ShowGlobalSnackbarMutation`
 * are used in any component where you want to fire up the snackbar.
 *
 * Example:
 * ```
   const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    ...
    <button onClick={() => {
                    showGlobalSnackbar({
                        variables: { message: 'Successfully, saved!', variant: 'success', key: 'PAGENAME_OR_DATAID' },
                    })
                }}
            >
                Info
            </button>
    ...
 * ```
 */
export interface ShowGlobalSnackbarRequest {
    showGlobalSnackbar: void
}

export interface ShowGlobalSnackbarMutationProps {
    message: string
    variant: keyof typeof variantIcon
    id: string
}

export const ShowGlobalSnackbarMutation = gql`
    mutation showGlobalSnackbarMutation(
        $message: String!
        $variant: String!
        $id: String!
    ) {
        showGlobalSnackbar(message: $message, variant: $variant, id: $id)
            @client
    }
`

export interface IGlobalSnackbarPropsQuery
    extends Partial<ShowGlobalSnackbarMutationProps> {
    open: boolean
}

export interface GlobalSnackbarProps extends IGlobalSnackbarPropsQuery {
    hideGlobalSnackbar: () => void
}

export interface GlobalSnackbarPropsQueryResponse {
    globalSnackbarProps: IGlobalSnackbarPropsQuery
}

export const HideGlobalSnackbarMutation = gql`
    mutation hideGlobalSnackbarMutation {
        hideGlobalSnackbar @client
    }
`

export const GlobalSnackbarPropsQuery = gql`
    query globalSnackbarPropsQuery {
        globalSnackbarProps @client {
            message
            variant
            id
            open
        }
    }
`

export const GlobalSnackbarQueryMutationResolver = {
    Mutation: {
        showGlobalSnackbar: (
            _root,
            { message, variant, id }: ShowGlobalSnackbarMutationProps,
            { cache }
        ) => {
            if (variant === 'error' && console) {
                console.error(message)
            }
            cache.writeQuery({
                query: GlobalSnackbarPropsQuery,
                data: {
                    globalSnackbarProps: {
                        __typename: 'GlobalSnackbar',
                        message,
                        variant,
                        id,
                        open: true,
                    },
                },
            })
        },
        hideGlobalSnackbar: (_root: any, _variable: any, { cache }: any) => {
            // when there is no cache, return using catch block
            try {
                const { globalSnackbarProps } = cache.readQuery({
                    query: GlobalSnackbarPropsQuery,
                })
                if (!globalSnackbarProps?.open) return
                cache.writeQuery({
                    query: GlobalSnackbarPropsQuery,
                    data: {
                        globalSnackbarProps: {
                            ...globalSnackbarProps,
                            open: false,
                        },
                    },
                })
            } catch (_e) {
                return
            }
        },
    },
}

/**
 * This is used to render the main content of the snackbar.  It checks if the message has '|'.
 * If it has a pipe, it renders an unordered list.
 * @param param0 message to display.
 */
const MessageContent = ({ message }: { message?: string }) => {
    const messages = message?.split('|') ?? ['']
    return (
        <div data-testid="clientSnackbar" style={{ display: 'flex' }}>
            {messages.length > 1 ? (
                <ul>
                    {messages.map((currentMessage, idx) => (
                        <li key={idx}>{currentMessage}</li>
                    ))}
                </ul>
            ) : (
                messages[0]
            )}
        </div>
    )
}

/**
 * This is added in the MainPage component.
 * To populate the properties, you will need to use `useQuery` and `useMutation` like this:
 * ``` jsx
    const { data: globalSnackbarPropsData } = useQuery<GlobalSnackbarPropsQueryResponse>(GlobalSnackbarPropsQuery)
    const [hideGlobalSnackbar] = useMutation(HideGlobalSnackbarMutation)
    ...
    {globalSnackbarPropsData && (
                <GlobalSnackbar
                    hideGlobalSnackbar={hideGlobalSnackbar}
                    open={globalSnackbarPropsData.globalSnackbarProps.open}
                    message={
                        globalSnackbarPropsData.globalSnackbarProps?.message
                    }
                    variant={
                        globalSnackbarPropsData.globalSnackbarProps?.variant
                    }
                />
            )}
    ...
 * ```
 */
export const GlobalSnackbar: React.FC<GlobalSnackbarProps> = ({
    open,
    hideGlobalSnackbar,
    message,
    variant,
    id,
}) => {
    const classes = useSnackbarWrapperStyles()
    const onSnackbarClose = (
        _: React.SyntheticEvent<any, Event>,
        reason: SnackbarCloseReason
    ) => {
        if (reason === 'clickaway') return
        hideGlobalSnackbar()
    }
    const onSnackbarButtonClose = () => {
        hideGlobalSnackbar()
    }

    return (
        <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={open}
            key={id}
            autoHideDuration={5000}
            onClose={onSnackbarClose}
            data-testid="globalSnackbarContainer"
        >
            <SnackbarContent
                className={clsx(
                    classes.snackbarContainer,
                    classes[variant ?? 'success']
                )}
                data-testid="globalSnackbarContent"
                aria-describedby="clientSnackbar"
                message={<MessageContent message={message} />}
                action={[
                    <IconButton
                        key="close"
                        aria-label="close"
                        color="inherit"
                        onClick={onSnackbarButtonClose}
                        data-testid="globalSnackbarCloseButton"
                    >
                        <CloseIcon className={classes.icon} />
                    </IconButton>,
                ]}
            />
        </Snackbar>
    )
}
